import React from "react";
import {
  Paper,
  Typography,
  Box,
  Divider,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { Functions, Scale, Verified } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const SummaryTable = ({
  totalShipAll,
  totalShoreAll,
  overallRatio,
  totalShipQualified,
  totalShoreQualified,
  vef,
  meanShipRatioLoaded,
  meanBLRatioLoaded,
}) => {
  const { t } = useTranslation("vef");
  const theme = useTheme();

  const DataRow = ({ label, value, unit = "" }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        py: 1,
        borderBottom: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.4),
      }}
    >
      <Typography
        variant="body2"
        fontWeight="600"
        color="text.secondary"
        sx={{ pr: 3 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight="800"
        sx={{ fontFamily: "monospace", fontSize: "1rem" }}
      >
        {typeof value === "number" && !label.includes("Ratio")
          ? value.toLocaleString()
          : value}{" "}
        {unit}
      </Typography>
    </Box>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        bgcolor: "background.paper",
        boxShadow: `0 4px 20px -5px ${alpha(theme.palette.common.black, 0.05)}`,
      }}
    >
      <Grid container>
        {/* Vänster sida: Maximerat utrymme (md={9}) */}
        <Grid sx={{ p: { xs: 3, md: 4 } }} size={{ xs: 12, md: 9.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 4 }}>
            <Functions color="primary" fontSize="small" />
            <Typography
              variant="subtitle2"
              fontWeight="900"
              sx={{ letterSpacing: 1, color: "text.secondary" }}
            >
              {t("summaryof20").toUpperCase()}
            </Typography>
          </Stack>

          <Grid container spacing={8}>
            {" "}
            {/* Kraftigt ökat kolumnavstånd */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="caption"
                color="primary"
                fontWeight="950"
                sx={{ display: "block", mb: 2, letterSpacing: 1.5 }}
              >
                ALL VOYAGES
              </Typography>
              <DataRow label={t("totalShip")} value={totalShipAll} unit="m³" />
              <DataRow
                label={t("totalShore")}
                value={totalShoreAll}
                unit="m³"
              />
              <DataRow
                label={t("overallshipshore")}
                value={overallRatio.toFixed(5)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography
                variant="caption"
                color="primary"
                fontWeight="950"
                sx={{ display: "block", mb: 2, letterSpacing: 1.5 }}
              >
                QUALIFIED DATA
              </Typography>
              <DataRow
                label={t("totalShipQualified")}
                value={totalShipQualified}
                unit="m³"
              />
              <DataRow
                label={t("totalShoreQualified")}
                value={totalShoreQualified}
                unit="m³"
              />
              <Box
                sx={{ display: "flex", justifyContent: "space-between", py: 1 }}
              >
                <Typography
                  variant="caption"
                  fontWeight="800"
                  color="text.disabled"
                >
                  LIMITS (±0.30%)
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight="800"
                  color="text.disabled"
                  sx={{ fontFamily: "monospace" }}
                >
                  {meanBLRatioLoaded.toFixed(5)} —{" "}
                  {meanShipRatioLoaded.toFixed(5)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>

        {/* Höger sida: "Floated" Box (md={2.5}) */}
        <Grid
          size={{ xs: 12, md: 2.5 }}
          sx={{
            ml: "auto",
            bgcolor: alpha(theme.palette.primary.main, 0.015),
            borderLeft: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.08),
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            p: 3,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              mb: 2,
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Scale
              color="primary"
              sx={{ fontSize: 40, opacity: 0.2, position: "absolute" }}
            />
            <Verified color="primary" sx={{ fontSize: 24, zIndex: 1 }} />
          </Box>

          <Typography
            variant="caption"
            fontWeight="900"
            color="primary"
            sx={{ mb: 0.5, letterSpacing: 1 }}
          >
            CURRENT VEF
          </Typography>

          <Typography
            variant="h3"
            fontWeight="950"
            color="text.primary"
            sx={{ letterSpacing: -1, mb: 1 }}
          >
            {vef ? vef.toFixed(5) : "—"}
          </Typography>

          <Divider
            sx={{
              width: "40%",
              mb: 2,
              borderColor: alpha(theme.palette.primary.main, 0.2),
            }}
          />

          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontWeight: 700,
              fontSize: "0.65rem",
              lineHeight: 1.3,
              maxWidth: 140,
            }}
          >
            {t("vesselExperienceFactor")}
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SummaryTable;
